/**
 * Types, constants and empty accumulators for the space demand simulation.
 * Consumed by {@link getSpaceDemandInWhPerM2} in space-demand.helpers.ts.
 */
import { Coordinate } from '@shared/models/geography.model';
import { EnergyZoneActivity } from './energy-activity.helpers';
import { ResolvedZoneProps } from './zone-schedule.helpers';
import {
  EnvironmentHourData,
  InsolationSurface,
  OrientedInsolationSurface,
} from './weather.models';

/**
 * Annual energy figures for a single zone (Wh/m² of zone area).
 * All values are accumulated over 8760 hours.
 */
export type ZoneAnnual = {
  /** Signed transmission balance (diagnostics) */
  transmission: number;
  /** Signed infiltration balance (diagnostics) */
  airleakage: number;
  /** Signed ventilation balance (diagnostics) */
  ventilation: number;
  /** Transmission when indoor > outdoor (heating direction, ≥ 0) */
  transmissionHeat: number;
  /** Transmission when indoor < outdoor (cooling direction, ≥ 0) */
  transmissionCool: number;
  airleakageHeat: number;
  airleakageCool: number;
  ventilationHeat: number;
  ventilationCool: number;
  /** Total solar insolation on zone windows (Wh/m²) */
  insolation: number;
  /** Portion of insolation that offsets heating demand (Wh/m²) */
  insolationHeat: number;
  /** Total occupant heat gain (Wh/m²) */
  occupant: number;
  /** Useful occupant heat gain (Wh/m²) */
  occupantHeat: number;
  /** Total activity/equipment heat gain (Wh/m²) */
  activity: number;
  /** Useful activity heat gain (Wh/m²) */
  activityHeat: number;
  /** Tap water recirculation heat gain (Wh/m²) */
  tapwater: number;
  /** Useful tap water heat gain (Wh/m²) */
  tapwaterHeat: number;
  /** Terminal heating delivered to the zone (Wh/m²), excludes postHeat */
  heat: number;
  /** Terminal cooling delivered to the zone (Wh/m²), excludes preCool */
  cool: number;
  /** Post heating coil energy (Wh/m²) */
  postHeat: number;
  /** Pre cooling coil energy (Wh/m²) */
  preCool: number;
  /** Heating demand that could not be met due to power cap (Wh/m²) */
  unmetHeat: number;
  /** Cooling demand that could not be met due to power cap (Wh/m²) */
  unmetCool: number;
};

/**
 * Annual energy figures for the whole building (Wh/m² of heated area).
 * Includes building-level additions not stored per zone.
 */
export type BuildingAnnual = ZoneAnnual & {
  /** Ventilation airing loss (Wh/m²) */
  airing: number;
  /** Regulation loss on delivered heat (Wh/m²) */
  regulation: number;
  /** Sum of heating degree hours over the year (K·h) */
  degreeHoursHeat: number;
  /** Sum of cooling degree hours over the year (K·h) */
  degreeHoursCool: number;
};

/**
 * Mutable per-zone state used during the hourly simulation.
 */
export type ZoneState = {
  act: EnergyZoneActivity;
  area: number;
  tempIn: number;
  /** The zone's transmission coefficient per m² (W/m²·K) */
  Htr_WperK_m2: number;
  /** Accumulated annual results (Wh/m² of zone area) */
  annual: ZoneAnnual;
  /** Share of the building window area allocated to this zone (0-1) */
  windowShare: number;
  /** Precomputed oriented surfaces for the zone's own windows, if any */
  orientedWindows?: OrientedInsolationSurface[];
  /** Precomputed resolved zone properties for each hour (index 0–8759) */
  propsTimeline: ResolvedZoneProps[];
  /** Precomputed occupant heat effect (W per occupant) for this zone's activity */
  occupantGainWPerOccupant: number;
  /** Precomputed activity/equipment heat gain (W/m²) before schedule multiplier */
  activityGainWPerM2Base: number;
};

/** Per-zone summary returned from {@link getSpaceDemandInWhPerM2} */
export type SpaceDemandZoneSummary = {
  activity: EnergyZoneActivity['type'];
  /** Zone floor area (m²) */
  area: number;
  /** Indoor temperature after the last simulated hour (°C) */
  finalTin_C: number;
  /** Zone transmission coefficient per m² (W/m²·K) */
  Htr_WperK_m2: number;
  /** Annual results per m² of zone area */
  annualWhPerM2: ZoneAnnual;
};

/** Full output of {@link getSpaceDemandInWhPerM2} */
export type SpaceDemandResult = {
  /** Total heated floor area (m²) */
  heatedArea: number;
  zones: SpaceDemandZoneSummary[];
  building: {
    /** Building transmission per heated area (W/m²·K), i.e. Um */
    Um: number;
    /** Warmest outdoor hour during the heating season (°C), or null */
    limitTemp: number | null;
    /** Hours where any zone delivered heat */
    heatingHours: number;
    /** Hours where any zone delivered cooling */
    coolingHours: number;
    /** Building annual results per m² of heated area */
    annualWhPerM2: BuildingAnnual;
  };
};

/** When the post heating coil is allowed to run */
export type PostHeatingStrategy = 'alwaysMinSAT' | 'onlyInHeatMode';
/** When the pre cooling coil is allowed to run */
export type PreCoolingStrategy = 'onlyInCoolMode' | 'alwaysMaxSAT';

export type SpaceDemandVentilationOptions = {
  postHeatingStrategy?: PostHeatingStrategy; // minimum Supply Air Temperature (SAT)

  preCoolingStrategy?: PreCoolingStrategy; // maximum Supply Air Temperature (SAT)

  /** Heat rise caused by fan (K) */
  fanHeatRiseK?: number;

  /** Ventilation flow in m³/s·m² */
  ventFlowPerM2?: number;

  /** Ventilation area (m²) */
  area: number;

  /** Heat recovery efficiency (0-1) */
  heatRecoveryEfficiency: number;

  /** Post heating temperature (°C) */
  postHeatingTemp?: number;

  /** Pre cooling temperature (°C) */
  preCoolingTemp?: number;
};

/** Zone comfort state for one hour */
export type DemandMode = 'heat' | 'cool' | 'off';

/** Input to {@link getSpaceDemandInWhPerM2} */
export type SpaceDemandOptions = {
  /** Combined options from air handling unit elements */
  ventilation: SpaceDemandVentilationOptions;
  insolationSurfaces: InsolationSurface[];
  environmentHours: EnvironmentHourData[];
  activities: EnergyZoneActivity[];

  tapWaterRecirculationGainInWPerHeatedArea?: number;
  thermalBridgesLossFactor?: number;
  airLeakageQ50InLPerS?: number;
  C_eff?: number;
  heatPowerLimitWPerM2?: number;
  coolPowerLimitWPerM2?: number;
  // Airing heating consumption per heated area (kWh/m2)
  airingLossInKwhPerHeatedArea?: number;
  // Regulation loss factor, how much heating that is lost due to regulation (e.g. 10% = 0.1)
  regulationLossFactor?: number;
  hrBypassHysteresisK?: number;

  /** Building coordinate, used to calculate time zone */
  buildingCoordinate?: Coordinate;

  /** Ventilation flow in m³/s·m² */
  ventFlow?: number;

  /** Transmission losses per m²·K (W/m²·K) */
  transmissionLossesWPerM2K: number;

  /** Air leakage losses per m²·K (W/m²·K) */
  airLeakageLossesWPerM2K: number;
};

export const EMPTY_ZONE_ANNUAL: ZoneAnnual = Object.freeze({
  transmission: 0,
  airleakage: 0,
  ventilation: 0,
  transmissionHeat: 0,
  transmissionCool: 0,
  airleakageHeat: 0,
  airleakageCool: 0,
  ventilationHeat: 0,
  ventilationCool: 0,
  insolation: 0,
  insolationHeat: 0,
  occupant: 0,
  occupantHeat: 0,
  activity: 0,
  activityHeat: 0,
  tapwater: 0,
  tapwaterHeat: 0,
  heat: 0,
  cool: 0,
  postHeat: 0,
  preCool: 0,
  unmetHeat: 0,
  unmetCool: 0,
});

export const EMPTY_BUILDING_ANNUAL: BuildingAnnual = Object.freeze({
  ...EMPTY_ZONE_ANNUAL,
  airing: 0,
  regulation: 0,
  degreeHoursHeat: 0,
  degreeHoursCool: 0,
});

/**
 * Return a zeroed result when no zones can be computed.
 * @param transmissionLossesWPerM2K Building Um value
 * @returns Zeroed space demand result
 */
export const createEmptySpaceDemandResult = (
  transmissionLossesWPerM2K: number,
): SpaceDemandResult => ({
  heatedArea: 0,
  zones: [],
  building: {
    Um: transmissionLossesWPerM2K,
    limitTemp: null,
    heatingHours: 0,
    coolingHours: 0,
    annualWhPerM2: { ...EMPTY_BUILDING_ANNUAL },
  },
});
